// Developer-options escape hatch for reviewing every variant of the Access page.
//
// The page branches on data that takes real purchases to produce (a Stripe subscription, a
// cancelled App Store one, a BLIK recurring payment, a lifetime product…), so without this the
// variants were only reviewable with one dedicated account each. The developer options panel
// writes the chosen fixture here and AccessContainer swaps it in for the GraphQL response.
//
// The panel itself only renders outside production (DeveloperOptionsContainer returns null
// there), but this helper is compiled into every build, so it also refuses to act on a
// production bundle. Mirrors `Tracker/helpers/devForcedError`.
import type {
  ActiveProductsData,
  ActiveSubscription,
  ActiveSubscriptionOperator,
} from '@front-activerse/common-components';

export const DEV_ACCESS_FIXTURE_STORAGE_KEY = 'developerOptionsAccessFixture';

// Subscription operators × states first (every Figma frame on the Access page), then the
// one-off / lifetime products, a mix, and the empty state.
const SUBSCRIPTION_OPERATORS = ['stripe', 'appStore', 'googlePlay', 'blik'] as const;
// BLIK recurring payments have no trial period.
const SUBSCRIPTION_STATES = ['', 'Trial', 'Canceled'] as const;

type OperatorFixture =
  `${(typeof SUBSCRIPTION_OPERATORS)[number]}${(typeof SUBSCRIPTION_STATES)[number]}`;

const SUBSCRIPTION_FIXTURES = SUBSCRIPTION_OPERATORS.flatMap((operator) =>
  SUBSCRIPTION_STATES.filter((state) => !(operator === 'blik' && state === 'Trial')).map(
    (state) => `${operator}${state}` as OperatorFixture
  )
);

export const DEV_ACCESS_FIXTURES = [
  'off',
  ...SUBSCRIPTION_FIXTURES,
  'onlyAi',
  'lifetime',
  'timed',
  'mixed',
  'none',
] as const;

export type DevAccessFixture = (typeof DEV_ACCESS_FIXTURES)[number];

/** ISO date `days` from today, in local time (the backend's DateType is a plain Y-m-d too). */
export const isoDateFromToday = (days: number, today: Date = new Date()): string => {
  const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() + days);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

// Dates are relative to today so the page reads like a real account rather than a far-future
// placeholder. They are resolved once, when the module loads. The e2e screenshots mask every
// date line for that reason. Lifetime keeps the backend's own sentinel date; it is never shown.
export const FIXTURE_DAYS = {
  /** Monthly subscription: next renewal in a month. */
  renewal: 30,
  /** Product access dates echo the renewal plus the backend grace period. */
  coveredBySubscription: 37,
  /** A trial is short-lived: first payment in a week. */
  trial: 7,
  /** Cancelled subscription: access runs out in under two weeks. */
  subscriptionEnds: 12,
  /** Cancelled a couple of days ago. */
  canceledAt: -2,
  /** One-off purchase of meals/workouts. */
  timedAccess: 45,
  /** Shorter AI add-on. */
  timedAiAccess: 20,
  /** Lapsed a month ago. */
  expired: -30,
} as const;

const RENEWAL = isoDateFromToday(FIXTURE_DAYS.renewal);
const COVERED_BY_SUBSCRIPTION = isoDateFromToday(FIXTURE_DAYS.coveredBySubscription);
const TIMED_ACCESS = isoDateFromToday(FIXTURE_DAYS.timedAccess);
const TIMED_AI_ACCESS = isoDateFromToday(FIXTURE_DAYS.timedAiAccess);
const EXPIRED = isoDateFromToday(FIXTURE_DAYS.expired);
const LIFETIME = '2049-09-05';

const noAccess: ActiveProductsData = {
  diet: { accessTo: EXPIRED, lifetimeAccess: false },
  videoWorkout: { accessTo: null, lifetimeAccess: false },
  aiAccess: { accessTo: null, hasAccess: false },
  subscription: { hasWorkoutAccess: false },
  activeSubscriptions: [],
};

const subscription = (overrides: Partial<ActiveSubscription>): ActiveSubscription => ({
  id: 9001,
  givesAccessToDiet: true,
  givesAccessToVideoWorkouts: true,
  givesAccessToAi: false,
  renewalDate: RENEWAL,
  operator: 'Stripe',
  isTrial: false,
  isCanceled: false,
  canceledAt: null,
  ...overrides,
});

// While a subscription runs, the backend echoes its renewal date (+ grace) into the product
// access dates — reproduced here so the fixture exercises the "covered by subscription" rule.
const withSubscription = (sub: ActiveSubscription): ActiveProductsData => ({
  diet: {
    accessTo: sub.givesAccessToDiet ? COVERED_BY_SUBSCRIPTION : EXPIRED,
    lifetimeAccess: false,
  },
  videoWorkout: {
    accessTo: sub.givesAccessToVideoWorkouts ? COVERED_BY_SUBSCRIPTION : null,
    lifetimeAccess: false,
  },
  aiAccess: {
    accessTo: sub.givesAccessToAi ? COVERED_BY_SUBSCRIPTION : null,
    hasAccess: sub.givesAccessToAi,
  },
  subscription: { hasWorkoutAccess: sub.givesAccessToVideoWorkouts },
  activeSubscriptions: [sub],
});

const OPERATOR_BY_FIXTURE: Record<
  (typeof SUBSCRIPTION_OPERATORS)[number],
  ActiveSubscriptionOperator
> = {
  stripe: 'Stripe',
  appStore: 'AppStore',
  googlePlay: 'GooglePlay',
  blik: 'Blik',
};

const TRIAL = { isTrial: true, renewalDate: isoDateFromToday(FIXTURE_DAYS.trial) };
const CANCELED = {
  isCanceled: true,
  canceledAt: isoDateFromToday(FIXTURE_DAYS.canceledAt),
  renewalDate: isoDateFromToday(FIXTURE_DAYS.subscriptionEnds),
};

const subscriptionFixtures = Object.fromEntries(
  SUBSCRIPTION_FIXTURES.map((name) => {
    const operatorKey = SUBSCRIPTION_OPERATORS.find((op) => name.startsWith(op));
    const operator = OPERATOR_BY_FIXTURE[operatorKey as (typeof SUBSCRIPTION_OPERATORS)[number]];
    const state = name.endsWith('Trial') ? TRIAL : name.endsWith('Canceled') ? CANCELED : {};
    // Store subscriptions are sold as the full bundle; BLIK as meals only.
    const bundle =
      operator === 'Blik'
        ? { givesAccessToVideoWorkouts: false }
        : operator === 'Stripe'
        ? {}
        : { givesAccessToAi: true };
    return [name, withSubscription(subscription({ operator, ...bundle, ...state }))];
  })
) as Record<OperatorFixture, ActiveProductsData>;

const FIXTURES: Record<Exclude<DevAccessFixture, 'off'>, ActiveProductsData> = {
  ...subscriptionFixtures,
  onlyAi: withSubscription(
    subscription({
      operator: 'AppStore',
      givesAccessToDiet: false,
      givesAccessToVideoWorkouts: false,
      givesAccessToAi: true,
    })
  ),
  lifetime: {
    diet: { accessTo: LIFETIME, lifetimeAccess: true },
    videoWorkout: { accessTo: LIFETIME, lifetimeAccess: true },
    aiAccess: { accessTo: null, hasAccess: false },
    subscription: { hasWorkoutAccess: true },
    activeSubscriptions: [],
  },
  timed: {
    diet: { accessTo: TIMED_ACCESS, lifetimeAccess: false },
    videoWorkout: { accessTo: TIMED_ACCESS, lifetimeAccess: false },
    aiAccess: { accessTo: TIMED_AI_ACCESS, hasAccess: true },
    subscription: { hasWorkoutAccess: true },
    activeSubscriptions: [],
  },
  mixed: {
    diet: { accessTo: COVERED_BY_SUBSCRIPTION, lifetimeAccess: false },
    videoWorkout: { accessTo: LIFETIME, lifetimeAccess: true },
    aiAccess: { accessTo: TIMED_AI_ACCESS, hasAccess: true },
    subscription: { hasWorkoutAccess: true },
    activeSubscriptions: [subscription({ givesAccessToVideoWorkouts: false })],
  },
  none: noAccess,
};

/**
 * Returns the fixture selected in developer options, or `null` when nothing is selected (the
 * normal case) — callers keep the real response then.
 */
export const getDevAccessFixture = (): ActiveProductsData | null => {
  if (import.meta.env.VITE_REACT_APP_ENV === 'prod') return null;
  try {
    const selected = localStorage.getItem(DEV_ACCESS_FIXTURE_STORAGE_KEY);
    if (!selected || selected === 'off') return null;
    return FIXTURES[selected as Exclude<DevAccessFixture, 'off'>] ?? null;
  } catch {
    // Private-mode Safari can throw on localStorage access; never break the real page over it.
    return null;
  }
};

export interface DevAccessOverridable {
  loading: boolean;
  error: boolean;
  data: ActiveProductsData | null;
}

/**
 * Substitutes the selected fixture for a real connector result when there is one, so the page
 * renders it as a successful, finished query.
 */
export const withDevAccessFixture = <T extends DevAccessOverridable>(result: T): T => {
  const fixture = getDevAccessFixture();
  return fixture ? { ...result, loading: false, error: false, data: fixture } : result;
};
